import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { NavigateFunction } from "react-router-dom";
import { getCardType } from "@/utils/functions";
import useCybersourceService from "@/hooks/useCybersourceService";
import moment from "moment";
import { changePaymentMethod, getCustomerData, paymentCard, setCustomer } from "@/redux/ducks/customer";
import { ReducersTypes } from "@/redux/reducers/Models";

const creditCardSchema = z.object({
  number: z.string().refine(val => getCardType(val) && val?.length >= 18, "Número inválido"),
  name: z.string().refine(val => val.length > 1 && /\D/.test(val ?? ""), "Nome inválido"),
  expiration: z.string().refine(
    (val) => {
      if (!/^\d{2}\/\d{4}$/.test(val)) {
        return false;
      }
      const [month] = val.split('/');
      const monthNum = parseInt(month, 10);
    
      if (monthNum < 1 || monthNum > 12) {
        return false;
      }
      
      const expiration = moment(val, "MM/YYYY", true);
      return expiration.isValid() && moment().diff(expiration, 'M') < 0;
    },
    (val) => {
      if (!/^\d{2}\/\d{4}$/.test(val)) {
        return { message: "Por favor, insira o ano de vencimento com 4 dígitos" };
      }
      
      const [month, year] = val.split('/');
      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);
      const currentYear = moment().year();
      
      if (monthNum < 1 || monthNum > 12) {
        if (yearNum < currentYear) {
          return { message: "Mês e ano de vencimento inválidos. Insira um mês de vencimento válido e um ano de vencimento futuro." };
        }
        return { message: "Mês de vencimento inválido." };
      }
      
      const expiration = moment(val, "MM/YYYY", true);
      if (!expiration.isValid() || moment().diff(expiration, 'M') >= 0) {
        return { message: "Ano de vencimento inválido: o cartão informado está expirado" };
      }
      
      return { message: "Ano de vencimento inválido: o cartão informado está expirado" };
    }
  ),
  save: z.boolean(),
  autoPayment: z.boolean(),
});

type State = {
  modalOpen: boolean;
  loading: boolean;
  error: boolean;
  errorChangeMethod: boolean;
  success: boolean;
  confirmationModalOpen: boolean;
  disableAutoPaymentModalOpen: boolean;
  attemptCount: number;
};

export default function useCreditCardForm({
  navigate,
  changeMethod = false,
  isAutoPaymentEnabled = true,
}: { 
  navigate: NavigateFunction;
  changeMethod?: boolean;
  isAutoPaymentEnabled?: boolean;
}) {
  const dispatch = useDispatch();
  const customer = useSelector((state: ReducersTypes) => state.customerReducer);
  
  const { addCyberSourceScripts, getSessionId } = useCybersourceService();

  const [ui, setUi] = useState<State>({
    modalOpen: false,
    loading: false,
    error: false,
    errorChangeMethod: false,
    success: false,
    confirmationModalOpen: false,
    disableAutoPaymentModalOpen: false,
    attemptCount: 0
  });

  const unifiedSchema = creditCardSchema;
  type FormData = z.infer<typeof unifiedSchema>;

  const defaultValues: FormData = {
    number: "",
    name: "",
    expiration: "",
    save: false,
    autoPayment: customer?.account?.paymentMode === "2" || false,
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onChange",
    defaultValues,
  });

  const allFields = watch();

  const toggleModal = useCallback(() => {
    setUi(prev => ({ 
      ...prev, 
      modalOpen: !prev.modalOpen
    }));
  }, []);

  const resetAttemptCount = useCallback(() => {
    setUi(prev => ({ ...prev, attemptCount: 0 }));
  }, []);

  const toggleConfirmationModal = useCallback(() => {
    setUi(prev => ({ ...prev, confirmationModalOpen: !prev.confirmationModalOpen }));
  }, []);

  const handleChangePaymentMethod = useCallback((success: boolean) => {
    setUi(prev => ({ ...prev, loading: false }));

		dispatch(
      getCustomerData()
    );

		if (!success) {
      setUi(prev => ({ ...prev, errorChangeMethod: true }));
      toggleModal();
			return;
		}

    if (changeMethod) {
      setUi(prev => ({ ...prev, success: true }));
      toggleModal();
      return;
    }
    
    navigate("/payment/finished");
    reset();
  }, [dispatch, navigate, reset, toggleModal, changeMethod]);

  const changePayment = useCallback(() => {
		const visitorId = getSessionId();

		dispatch(
      changePaymentMethod({
        visitorId: visitorId!,
        card: {
          number: allFields.number,
          name: allFields.name,
          expiration: allFields.expiration,
          autoPayment: isAutoPaymentEnabled ? allFields.autoPayment : false,
        },
        callback: handleChangePaymentMethod,
      })
    );
  }, [dispatch, handleChangePaymentMethod, getSessionId, allFields, isAutoPaymentEnabled]);

  const handlePayment = useCallback((success: boolean, data?: { sqPagamento?: string }) => {
    if (!success) {
      setUi(prev => ({ 
        ...prev, 
        loading: false, 
        error: true,
        attemptCount: prev.attemptCount + 1
      }));
      toggleModal();
      return;
    }

    if (data?.sqPagamento) {
      dispatch(setCustomer({ sqPagamento: data.sqPagamento }));
    }

    if (!allFields.save) {
      setUi(prev => ({ ...prev, loading: false, attemptCount: 0 }));

			dispatch(
        getCustomerData()
      );

      navigate("/payment/finished");

			return;
		}

    changePayment();

  }, [toggleModal, allFields, changePayment, navigate, dispatch]);

  const handleConfirmAutoPayment = useCallback(() => {
    toggleConfirmationModal();
    setUi(prev => ({ ...prev, loading: true, error: false, errorChangeMethod: false }));
    
    
    if (changeMethod) {
      changePayment();
    } else {
      const visitorId = getSessionId();
      dispatch(
        paymentCard({
          visitorId: visitorId!,
          card: {
            number: allFields.number,
            name: allFields.name,
            expiration: allFields.expiration,
          },
          callback: handlePayment,
        })
      );
    }
  }, [toggleConfirmationModal, changeMethod, changePayment, getSessionId, dispatch, allFields, handlePayment]);

  const handleCancelAutoPayment = useCallback(() => {
    toggleConfirmationModal();
    // Desmarcar o checkbox de autopayment
    setValue("autoPayment", false);
  }, [toggleConfirmationModal, setValue]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;

      // Se o pagamento direto estiver habilitado e a feature flag estiver ativa, mostrar modal de confirmação
      if (data.autoPayment && isAutoPaymentEnabled) {
        setUi(prev => ({ ...prev, confirmationModalOpen: true }));
        return;
      }

      setUi(prev => ({ ...prev, loading: true, error: false, errorChangeMethod: false }));

      if (changeMethod) {
        changePayment();
        return;
      }
    
      const visitorId = getSessionId();

      dispatch(
        paymentCard({
          visitorId: visitorId!,
          card: {
            number: data.number,
            name: data.name,
            expiration: data.expiration,
          },
          callback: handlePayment,
        })
      );
    },
    [ui.loading, dispatch, handlePayment, getSessionId, changePayment, changeMethod, isAutoPaymentEnabled]
  );

  useEffect(() => {
    addCyberSourceScripts();
  }, []);

  const handleAutoPaymentChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setValue('save', true);
      setValue("autoPayment", true);
    } else {
      setValue('save', false);
      setValue("autoPayment", false);
    }

    if(!event.target.checked) {
      setUi(prev => ({ ...prev, disableAutoPaymentModalOpen: true }));
    }
  }, [setValue]);

  const handleConfirmDisableAutoPaymentModal = useCallback(() => {
    setUi(prev => ({ ...prev, disableAutoPaymentModalOpen: false }));
  }, [toggleConfirmationModal, setValue]);

  const handleCancelDisableAutoPaymentModal = useCallback(() => {
    setUi(prev => ({ ...prev, disableAutoPaymentModalOpen: false }));
    setValue('autoPayment', true);
  }, [setValue]);

  return {
    toggleModal,
    toggleConfirmationModal,
    resetAttemptCount,
    isVisibleModal: ui.modalOpen,
    isVisibleConfirmationModal: ui.confirmationModalOpen,
    isVisibleDisableAutoPaymentModal: ui.disableAutoPaymentModalOpen,
    isLoading: ui.loading,
    isDisabled: !isValid || ui.loading,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    error: ui.error,
    errorChangeMethod: ui.errorChangeMethod,
    success: ui.success,
    attemptCount: ui.attemptCount,
    handleConfirmAutoPayment,
    handleCancelAutoPayment,
    handleAutoPaymentChange,
    handleConfirmDisableAutoPaymentModal,
    handleCancelDisableAutoPaymentModal,
    customer,
    watch,
  };
} 